import { useEffect, useMemo, useState } from 'react'
import { format, isSameDay, isToday, isTomorrow } from 'date-fns'
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  Inbox as InboxIcon,
  Trash2,
  Loader2,
  RotateCcw,
  ExternalLink,
  Link2Off,
} from 'lucide-react'
import clsx from 'clsx'
import { useStore } from '@/lib/store'
import { useUI } from '@/lib/ui'
import { PLATFORMS } from '@/lib/platforms'
import type { Post, PostDelivery, PostStatus } from '@/lib/types'
import { retryPost } from '@/lib/data'
import { PostPreview } from '@/features/composer/PostPreview'
import { ChannelAvatar, channelProfileSource, ProfileImage } from '@/components/ChannelAvatar'

type StatusFilter = PostStatus | 'all'

const STATUS_META: Record<PostStatus, { label: string; dot: string }> = {
  scheduled: { label: 'Scheduled', dot: 'bg-brand-500' },
  publishing: { label: 'Publishing', dot: 'bg-accent-amber' },
  retrying: { label: 'Retrying', dot: 'bg-accent-amber' },
  partial: { label: 'Partly failed', dot: 'bg-accent-amber' },
  draft: { label: 'Drafts', dot: 'bg-ink-400' },
  published: { label: 'Published', dot: 'bg-accent-green' },
  failed: { label: 'Failed', dot: 'bg-accent-red' },
}

const PILL_STYLES: Record<PostStatus, string> = {
  scheduled: 'text-brand-600 border-brand-200 bg-brand-50',
  publishing: 'text-accent-amber border-accent-amber/30 bg-accent-amber/10',
  retrying: 'text-accent-amber border-accent-amber/30 bg-accent-amber/10',
  partial: 'text-accent-amber border-accent-amber/30 bg-accent-amber/10',
  draft: 'text-ink-500 border-line bg-surface',
  published: 'text-accent-green border-accent-green/30 bg-accent-green/10',
  failed: 'text-accent-red border-accent-red/30 bg-accent-red/10',
}

function dayLabel(d: number) {
  if (isToday(d)) return `Today · ${format(d, 'MMM d')}`
  if (isTomorrow(d)) return `Tomorrow · ${format(d, 'MMM d')}`
  return format(d, 'EEEE · MMM d')
}

function groupPostsByDay(posts: Post[]) {
  const groups: { day: number; items: Post[] }[] = []
  for (const post of posts) {
    const last = groups[groups.length - 1]
    if (last && isSameDay(last.day, post.scheduledAt)) last.items.push(post)
    else groups.push({ day: post.scheduledAt, items: [post] })
  }
  return groups
}

export function InboxPage() {
  const posts = useStore((s) => s.posts)
  const channels = useStore((s) => s.channels)
  const deletePost = useStore((s) => s.deletePost)
  const openComposer = useUI((s) => s.openComposer)
  const openConnect = useUI((s) => s.openConnect)

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [channelFilter, setChannelFilter] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pastOpen, setPastOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const filtered = useMemo(() => {
    return posts
      .filter((p) => (statusFilter === 'all' ? true : p.status === statusFilter))
      .filter((p) => (channelFilter ? p.channelIds.includes(channelFilter) : true))
      .filter((p) =>
        query.trim() ? p.content.toLowerCase().includes(query.trim().toLowerCase()) : true,
      )
      .sort((a, b) => a.scheduledAt - b.scheduledAt)
  }, [posts, statusFilter, channelFilter, query])

  const upcomingPosts = useMemo(
    () => filtered.filter((post) => post.scheduledAt >= now),
    [filtered, now],
  )
  const pastPosts = useMemo(
    () => filtered.filter((post) => post.scheduledAt < now).reverse(),
    [filtered, now],
  )
  const upcomingGroups = useMemo(() => groupPostsByDay(upcomingPosts), [upcomingPosts])
  const pastGroups = useMemo(() => groupPostsByDay(pastPosts), [pastPosts])

  const selected = useMemo(
    () => filtered.find((p) => p.id === selectedId) ?? upcomingPosts[0] ?? null,
    [filtered, selectedId, upcomingPosts],
  )
  const selectedIsPast = selected ? selected.scheduledAt < now : false

  const removeSelectedPost = async () => {
    if (!selected || deletingId) return
    setDeletingId(selected.id)
    setDeleteError(null)
    try {
      await deletePost(selected.id)
      setSelectedId(null)
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Could not delete this post.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-paper">
      <header className="flex flex-wrap items-center gap-2 border-b border-line bg-paper px-4 py-3 sm:px-5">
        <div className="mr-auto flex items-center gap-2">
          <h1 className="text-base font-bold tracking-tight text-ink-900">Posts</h1>
          <span className="rounded-full border border-line bg-surface px-2 py-0.5 font-mono text-[10px] text-ink-500">
            {upcomingPosts.length}
          </span>
        </div>
        <div className="relative order-last w-full sm:order-none sm:w-[220px]">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search posts"
            aria-label="Search posts"
            className="h-9 w-full rounded-full border border-line bg-surface pl-9 pr-3 text-xs text-ink-900 outline-none placeholder:text-ink-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <CompactSelect
          label="Status"
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as StatusFilter)}
          options={[
            { value: 'all', label: 'All statuses' },
            ...(Object.keys(STATUS_META) as PostStatus[]).map((status) => ({
              value: status,
              label: STATUS_META[status].label,
            })),
          ]}
        />
        <CompactSelect
          label="Channel"
          value={channelFilter ?? 'all'}
          onChange={(value) => setChannelFilter(value === 'all' ? null : value)}
          options={[
            { value: 'all', label: 'All channels' },
            ...channels.map((channel) => ({ value: channel.id, label: channel.name })),
          ]}
        />
        <button
          onClick={() => openComposer()}
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-ink-900 px-3.5 text-xs font-semibold text-white shadow-sm transition hover:bg-ink-700"
        >
          <Plus size={14} /> New post
        </button>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(320px,36%)_minmax(0,1fr)] xl:grid-cols-[380px_minmax(520px,1fr)]">
        <section
          data-testid="posts-list"
          className={clsx(
            'min-h-0 flex-col overflow-hidden border-r border-line bg-paper',
            selectedId ? 'hidden lg:flex' : 'flex',
          )}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {upcomingGroups.length === 0 ? (
              <UpcomingEmptyState onCompose={() => openComposer()} />
            ) : (
              upcomingGroups.map((g) => (
                <div key={g.day}>
                  <div className="sticky top-0 z-10 border-b border-line/70 bg-paper/95 px-4 pb-2 pt-3 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-400 backdrop-blur">
                    {dayLabel(g.day)}
                  </div>
                  {g.items.map((p) => (
                    <PostRow
                      key={p.id}
                      post={p}
                      selected={selected?.id === p.id}
                      onClick={() => setSelectedId(p.id)}
                    />
                  ))}
                </div>
              ))
            )}
            {pastPosts.length > 0 && (
              <section className="border-b border-line" aria-label="Past posts">
                <button
                  type="button"
                  aria-expanded={pastOpen}
                  aria-controls="past-posts-list"
                  onClick={() => {
                    setPastOpen((open) => !open)
                    if (pastOpen && selectedIsPast) setSelectedId(null)
                  }}
                  className="flex w-full items-center gap-2 border-y border-line bg-surface/70 px-4 py-3 text-left transition hover:bg-panel"
                >
                  <ChevronRight
                    size={14}
                    className={clsx('text-ink-400 transition-transform', pastOpen && 'rotate-90')}
                  />
                  <span className="text-[12.5px] font-semibold text-ink-800">Past posts</span>
                  <span className="ml-auto rounded-full bg-inset px-2 py-0.5 font-mono text-[10px] text-ink-500 shadow-hairline">
                    {pastPosts.length}
                  </span>
                </button>
                {pastOpen && (
                  <div id="past-posts-list">
                    {pastGroups.map((group) => (
                      <div key={group.day}>
                        <div className="border-b border-line/70 bg-paper px-4 pb-2 pt-3 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-400">
                          {dayLabel(group.day)}
                        </div>
                        {group.items.map((post) => (
                          <PostRow
                            key={post.id}
                            post={post}
                            selected={selected?.id === post.id}
                            onClick={() => setSelectedId(post.id)}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        </section>

        <section
          data-testid="post-detail"
          className={clsx(
            'min-h-0 overflow-y-auto bg-panel/35',
            selectedId ? 'block' : 'hidden lg:block',
          )}
        >
          {selected ? (
            <PostDetail
              post={selected}
              onBack={() => setSelectedId(null)}
              onEdit={() => openComposer({ editId: selected.id, ...selected })}
              onDelete={() => void removeSelectedPost()}
              deleting={deletingId === selected.id}
              deleteError={deleteError}
              onConnect={openConnect}
            />
          ) : (
            <div className="grid h-full place-items-center p-8 text-center">
              <p className="text-sm text-ink-400">Select a post to see its details</p>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function CompactSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <label className="relative inline-flex h-9 items-center rounded-full border border-line bg-surface shadow-sm transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="h-full appearance-none rounded-full bg-transparent py-0 pl-3 pr-8 text-xs font-medium text-ink-700 outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={13}
        className="pointer-events-none absolute right-3 text-ink-400"
      />
    </label>
  )
}

function PostRow({
  post,
  selected,
  onClick,
}: {
  post: Post
  selected: boolean
  onClick: () => void
}) {
  const channels = useStore((s) => s.channels)
  const pcs = post.channelIds
    .map((id) => channels.find((c) => c.id === id))
    .filter((c): c is NonNullable<typeof c> => Boolean(c))
  const primary = pcs[0]
  const pill = PILL_STYLES[post.status]

  return (
    <button
      onClick={onClick}
      className={clsx(
        'relative flex w-full items-start gap-3 border-b border-line px-4 py-3 text-left transition',
        selected ? 'bg-brand-50 shadow-[inset_3px_0_0_theme(colors.brand.500)]' : 'hover:bg-panel',
      )}
    >
      {primary ? (
        <ChannelAvatar channel={primary} size="sm" />
      ) : (
        <span className="grid h-[30px] w-[30px] place-items-center rounded-[7px] bg-ink-200" />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-ink-900">
            {primary?.name ?? 'Post'}{' '}
            <span className="font-mono text-[10px] font-normal text-ink-400">
              {primary?.handle}
            </span>
          </span>
          <span className="shrink-0 whitespace-nowrap font-mono text-[10px] text-ink-400">
            {format(post.scheduledAt, isToday(post.scheduledAt) ? 'h:mm a' : 'MMM d')}
          </span>
        </div>
        <p className="line-clamp-2 text-xs leading-snug text-ink-600">
          {post.content || '(media post)'}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.05em]',
              pill,
            )}
          >
            <span className={clsx('h-1.5 w-1.5 rounded-full', STATUS_META[post.status].dot)} />
            {STATUS_META[post.status].label.replace(/s$/, '')}
          </span>
          {pcs.length > 1 && (
            <span className="font-mono text-[10px] text-ink-400">{pcs.length} channels</span>
          )}
        </div>
      </div>
    </button>
  )
}

function PostDetail({
  post,
  onBack,
  onEdit,
  onDelete,
  deleting,
  deleteError,
  onConnect,
}: {
  post: Post
  onBack: () => void
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
  deleteError: string | null
  onConnect: () => void
}) {
  const channels = useStore((s) => s.channels)
  const media = useStore((s) => s.media)
  const pcs = post.channelIds
    .map((id) => channels.find((c) => c.id === id))
    .filter((c): c is NonNullable<typeof c> => Boolean(c))
  const postMedia = post.mediaIds
    .map((id) => media.find((item) => item.id === id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
  const primary = pcs[0]
  const meta = STATUS_META[post.status]
  const [previewChannelId, setPreviewChannelId] = useState(primary?.id ?? '')
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setPreviewChannelId(primary?.id ?? '')
    setConfirmingDelete(false)
  }, [post.id, primary?.id])

  const previewChannel =
    pcs.find((channel) => channel.id === previewChannelId) ?? primary ?? null
  const canDelete = ['draft', 'scheduled', 'failed'].includes(post.status)
  // A published post is live on the networks. Editing it here would only
  // change the draft text, so the button would lie.
  const canEdit = post.status !== 'published'

  return (
    <article className="mx-auto w-full max-w-[820px] px-5 pb-12 pt-5 sm:px-7 lg:px-8 lg:pt-6">
      <div className="mb-4 flex items-start gap-3">
        <button
          onClick={onBack}
          aria-label="Back to posts"
          className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full border border-line bg-surface text-ink-600 lg:hidden"
        >
          <ArrowLeft size={15} />
        </button>
        <div className="min-w-0 flex-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-400">
            {meta.label.replace(/s$/, '')} · {primary ? PLATFORMS[primary.platform].name : 'Post'}
          </span>
          <h1 className="mt-1 text-xl font-bold leading-tight tracking-tight text-ink-900 sm:text-2xl">
            {post.content.split('\n')[0].slice(0, 80) || 'Untitled post'}
          </h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canDelete && !confirmingDelete && (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={deleting}
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-line bg-surface px-3 text-xs font-semibold text-accent-red shadow-sm transition hover:bg-paper disabled:opacity-50"
            >
              {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Delete
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-9 items-center rounded-full border border-line bg-surface px-4 text-xs font-semibold text-ink-900 shadow-sm transition hover:bg-paper"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {canDelete && confirmingDelete && (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2 rounded-xl border border-accent-red/25 bg-accent-red/5 px-3 py-2.5">
          <span className="mr-auto text-xs font-medium text-ink-700">Delete this post?</span>
          <button
            type="button"
            className="btn-subtle px-3 py-1.5 text-xs"
            onClick={() => setConfirmingDelete(false)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full bg-accent-red px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            onClick={onDelete}
            disabled={deleting}
          >
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            Confirm delete
          </button>
        </div>
      )}

      {deleteError && (
        <p className="mb-4 rounded-xl border border-accent-red/30 bg-accent-red/10 px-3 py-2 text-xs text-accent-red">
          {deleteError}
        </p>
      )}

      {/* schedule strip */}
      <div className="mb-4 grid overflow-hidden rounded-xl border border-line bg-surface shadow-sm sm:grid-cols-3">
        <div className="min-w-0 border-b border-line px-3 py-2.5 sm:border-b-0 sm:border-r">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-400">When</div>
          <div className="mt-1 whitespace-nowrap text-[13px] font-semibold text-brand-500">
            {format(post.scheduledAt, 'EEE, MMM d · h:mm a')}
          </div>
        </div>
        <div className="min-w-0 border-b border-line px-3 py-2.5 sm:border-b-0 sm:border-r">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-400">
            Status
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-ink-900">
            <span className={clsx('h-2 w-2 rounded-full', meta.dot)} />
            {meta.label.replace(/s$/, '')}
          </div>
        </div>
        <div className="min-w-0 px-3 py-2.5">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-400">
            Channels
          </div>
          <div className="mt-1 text-[13px] font-semibold text-ink-900">{pcs.length}</div>
        </div>
      </div>

      {post.deliveries && post.deliveries.length > 0 && (
        <DeliveryList post={post} deliveries={post.deliveries} onConnect={onConnect} />
      )}

      {/* target channels */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-400">
          Channels
        </span>
        {pcs.map((c) => {
          const p = PLATFORMS[c.platform]
          return (
            <span
              key={c.id}
              className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 py-1 pl-1.5 pr-2.5 text-xs font-medium text-brand-600"
            >
              <ProfileImage
                src={channelProfileSource(c)}
                name={c.name}
                background={c.avatarColor || p.gradient}
                size={16}
              />
              {c.handle}
            </span>
          )
        })}
        <button
          onClick={onConnect}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2.5 py-1 text-xs text-ink-400 transition hover:border-brand-500 hover:text-brand-500"
        >
          <Plus size={11} /> Add channel
        </button>
      </div>

      {/* preview card */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="mr-auto font-mono text-[10px] uppercase tracking-[0.08em] text-ink-400">
          Preview
        </span>
        {pcs.length > 1 &&
          pcs.map((channel) => (
            <button
              key={channel.id}
              onClick={() => setPreviewChannelId(channel.id)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition',
                previewChannel?.id === channel.id
                  ? 'border-ink-900 bg-ink-900 text-white'
                  : 'border-line bg-surface text-ink-600 hover:border-ink-300',
              )}
            >
              <span className="inline-flex size-3.5 items-center justify-center">
                {PLATFORMS[channel.platform].icon}
              </span>
              {PLATFORMS[channel.platform].name}
            </button>
          ))}
      </div>
      {previewChannel ? (
        <div className="w-full max-w-[560px]">
          <div className="mb-1.5 flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.08em] text-ink-400">
            <span className="inline-flex size-3.5 items-center justify-center">
              {PLATFORMS[previewChannel.platform].icon}
            </span>
            {PLATFORMS[previewChannel.platform].name} preview
          </div>
          <PostPreview
            platform={previewChannel.platform}
            channel={previewChannel}
            content={post.overrides?.[previewChannel.id]?.trim() || post.content}
            media={postMedia}
          />
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-line bg-surface p-8 text-center text-sm text-ink-400">
          Connect a channel to preview this post.
        </div>
      )}
    </article>
  )
}

const DELIVERY_META: Record<PostDelivery['status'], { label: string; pill: string; dot: string }> = {
  scheduled: { label: 'Scheduled', pill: PILL_STYLES.scheduled, dot: STATUS_META.scheduled.dot },
  publishing: { label: 'Publishing', pill: PILL_STYLES.publishing, dot: STATUS_META.publishing.dot },
  published: { label: 'Published', pill: PILL_STYLES.published, dot: STATUS_META.published.dot },
  failed: { label: 'Failed', pill: PILL_STYLES.failed, dot: STATUS_META.failed.dot },
  canceled: { label: 'Canceled', pill: PILL_STYLES.draft, dot: STATUS_META.draft.dot },
}

function deliveryLabel(d: PostDelivery) {
  if (d.status === 'scheduled' && d.attempts > 0) return 'Retrying'
  return DELIVERY_META[d.status].label
}

/**
 * One row per channel: where the post stands on that network, the live link
 * once it is up, the last error when it is not, and the way out (retry, or
 * reconnect when the token is the problem).
 */
function DeliveryList({
  post,
  deliveries,
  onConnect,
}: {
  post: Post
  deliveries: PostDelivery[]
  onConnect: () => void
}) {
  const channels = useStore((s) => s.channels)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const failed = deliveries.filter((d) => d.status === 'failed')
  const retryable = failed.filter((d) => d.error?.kind !== 'auth_error')

  useEffect(() => {
    setError(null)
    setBusy(null)
  }, [post.id])

  const retry = async (channelId?: string) => {
    setBusy(channelId ?? 'all')
    setError(null)
    try {
      await retryPost(post.id, channelId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not retry this post.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-line bg-surface shadow-sm" aria-label="Delivery by channel">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <span className="mr-auto font-mono text-[10px] uppercase tracking-[0.08em] text-ink-400">
          Delivery
        </span>
        {retryable.length > 0 && (
          <button
            type="button"
            onClick={() => retry()}
            disabled={busy !== null}
            className="inline-flex h-7 items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 text-[11px] font-semibold text-ink-900 transition hover:bg-paper disabled:opacity-50"
          >
            {busy === 'all' ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Retry all failed
          </button>
        )}
      </div>
      <ul className="divide-y divide-line">
        {deliveries.map((d) => {
          const channel = channels.find((c) => c.id === d.channelId)
          const platform = channel ? PLATFORMS[channel.platform] : null
          const meta = DELIVERY_META[d.status]
          const needsReconnect = d.status === 'failed' && d.error?.kind === 'auth_error'
          const rowBusy = busy === d.channelId || busy === 'all'
          return (
            <li key={d.channelId} className="flex flex-wrap items-start gap-x-3 gap-y-1.5 px-3 py-2.5">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {channel ? (
                  <ChannelAvatar channel={channel} size="sm" />
                ) : (
                  <span className="size-6 rounded-full bg-ink-100" aria-hidden="true" />
                )}
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold text-ink-900">
                    {channel ? channel.handle || channel.name : 'Removed channel'}
                  </div>
                  <div className="truncate text-[11px] text-ink-400">
                    {platform?.name ?? 'Unknown network'}
                    {d.status === 'published' && d.publishedAt
                      ? ` · Live ${format(d.publishedAt, 'MMM d, h:mm a')}`
                      : d.status === 'scheduled' && d.runAt
                        ? ` · ${d.attempts > 0 ? 'Next try' : 'Due'} ${format(d.runAt, 'MMM d, h:mm a')}`
                        : ''}
                  </div>
                </div>
              </div>
              <span
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  meta.pill,
                )}
              >
                <span className={clsx('h-1.5 w-1.5 rounded-full', meta.dot)} />
                {deliveryLabel(d)}
              </span>
              <div className="flex items-center gap-1.5">
                {d.url && (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-7 items-center gap-1 rounded-full border border-line bg-surface px-2.5 text-[11px] font-semibold text-ink-900 transition hover:bg-paper"
                  >
                    <ExternalLink size={12} /> View post
                  </a>
                )}
                {needsReconnect && (
                  <button
                    type="button"
                    onClick={onConnect}
                    className="inline-flex h-7 items-center gap-1 rounded-full border border-accent-red/30 bg-accent-red/10 px-2.5 text-[11px] font-semibold text-accent-red transition hover:bg-accent-red/15"
                  >
                    <Link2Off size={12} /> Reconnect
                  </button>
                )}
                {d.status === 'failed' && !needsReconnect && (
                  <button
                    type="button"
                    onClick={() => retry(d.channelId)}
                    disabled={busy !== null}
                    className="inline-flex h-7 items-center gap-1 rounded-full border border-line bg-surface px-2.5 text-[11px] font-semibold text-ink-900 transition hover:bg-paper disabled:opacity-50"
                  >
                    {rowBusy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    Retry
                  </button>
                )}
              </div>
              {d.error && d.status !== 'published' && (
                <p className="basis-full text-[12px] leading-snug text-ink-600">
                  <span className={clsx('font-semibold', d.status === 'failed' ? 'text-accent-red' : 'text-accent-amber')}>
                    {needsReconnect
                      ? 'Reconnect needed'
                      : d.status === 'failed'
                        ? `Failed after ${d.attempts} ${d.attempts === 1 ? 'try' : 'tries'}`
                        : `Try ${d.attempts} failed`}
                    :
                  </span>{' '}
                  {d.error.message}
                </p>
              )}
            </li>
          )
        })}
      </ul>
      {error && (
        <p className="border-t border-accent-red/30 bg-accent-red/10 px-3 py-2 text-xs text-accent-red">{error}</p>
      )}
    </section>
  )
}

function UpcomingEmptyState({ onCompose }: { onCompose: () => void }) {
  return (
    <div className="border-b border-line px-5 py-7 text-center">
      <div className="mx-auto max-w-xs">
        <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-500">
          <InboxIcon size={19} />
        </div>
        <h2 className="font-display text-sm font-bold text-ink-900">No upcoming posts</h2>
        <p className="mx-auto mt-1 text-xs text-ink-500">
          Schedule your next post, or open Past posts below.
        </p>
        <button
          onClick={onCompose}
          className="mx-auto mt-3 inline-flex h-8 items-center gap-1.5 rounded-full bg-ink-900 px-3 text-xs font-semibold text-white transition hover:bg-ink-700"
        >
          <Plus size={13} /> New post
        </button>
      </div>
    </div>
  )
}
